import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSuperChatLogStore } from "@/store/useSuperChatLogStore";
import { sessionFeedbackApi } from "@/apis/sessionFeedback";
import { SessionFeedback } from "@/apis/types/SuperChatLogTypes";

/**
 * 세션 상세 페이지 훅
 *
 * @description 세션 상세 페이지의 상태와 로직을 관리하는 커스텀 훅
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export const useSessionDetail = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState<boolean>(false);
  const [sessionFeedback, setSessionFeedback] =
    useState<SessionFeedback | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState<boolean>(false);

  const { currentSession, messages, loading, fetchSessionMessages } =
    useSuperChatLogStore();

  // 세션 피드백 조회 함수
  const fetchSessionFeedback = async (sessionId: string) => {
    setFeedbackLoading(true);
    try {
      console.log("[SESSION_DETAIL]", {
        action: "fetchSessionFeedback",
        sessionId,
      });

      const feedbackContent = await sessionFeedbackApi.getFeedback(
        parseInt(sessionId)
      );

      console.log("[SESSION_DETAIL]", {
        action: "fetchSessionFeedback",
        sessionId,
        feedbackContent,
        feedbackType: typeof feedbackContent,
        feedbackLength: feedbackContent?.length,
      });

      if (feedbackContent) {
        const feedback: SessionFeedback = {
          sessionId,
          content: feedbackContent,
          createdAt: new Date().toISOString(), // API에서 메타데이터를 제공하지 않으므로 임시값
        };
        setSessionFeedback(feedback);

        console.log("[SESSION_DETAIL]", {
          action: "fetchSessionFeedback",
          result: "success",
          hasFeedback: true,
          feedbackObject: feedback,
        });
      } else {
        setSessionFeedback(null);
        console.log("[SESSION_DETAIL]", {
          action: "fetchSessionFeedback",
          result: "success",
          hasFeedback: false,
        });
      }
    } catch (error: any) {
      console.error("[SESSION_DETAIL]", {
        action: "fetchSessionFeedback",
        error: error.message,
        sessionId,
      });
      setSessionFeedback(null);
    } finally {
      setFeedbackLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      // 세션 상세 조회 시 메시지도 함께 조회됨
      fetchSessionMessages(sessionId);
      // 피드백 데이터도 함께 조회
      fetchSessionFeedback(sessionId);
    }
  }, [sessionId, fetchSessionMessages]);

  const handleBack = () => {
    navigate("/chat/log");
  };

  // 탭 변경 핸들러
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // 피드백 모달 핸들러
  const handleOpenFeedbackModal = () => {
    setFeedbackModalOpen(true);
  };

  const handleCloseFeedbackModal = () => {
    setFeedbackModalOpen(false);
  };

  const handleFeedbackUpdated = (feedback: string | null) => {
    console.log("[SESSION_DETAIL]", {
      action: "feedbackUpdated",
      sessionId,
      hasFeedback: !!feedback,
    });

    // 피드백이 업데이트되면 다시 조회
    if (sessionId) {
      fetchSessionFeedback(sessionId);
    }
  };

  return {
    // 상태
    sessionId,
    currentSession,
    messages,
    loading,
    activeTab,
    feedbackModalOpen,
    sessionFeedback,
    feedbackLoading,

    // 핸들러
    handleBack,
    handleTabChange,
    handleOpenFeedbackModal,
    handleCloseFeedbackModal,
    handleFeedbackUpdated,
  };
};
